import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { Download } from "lucide-react";
import ToggleTab from "../Mod/ModTable/ChartTableToggle";
import ModHeader from "../Mod/ModTable/ModHeader";
import Modal from "../Mod/ModTable/Modal";
import { BidCreationTable, transformClassData } from "./BidCreationTable";
import BidCreationChart from "./BidCreationChart";
import { ErrorPopup } from "../../../../components/ErrorHandling";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { useMeritEngineNavigation } from "../useMeritEngineNavigation";
import MainHeader from "../DateSession";
import Tooltip from "../../../../components/tooltip";
import { ReactComponent as Edit } from "../../../../icons/edit_solid.svg";
import {
  getClassBid,
  fetchModChartClassGrouped,
  postClassBid,
} from "../../Utility/RestApi";
import loader from "../../../../components/recharts/loader.gif";

const DataTabs = ["chart", "table"];

export default function BidCreationIndex() {
  const { navigateToPage } = useMeritEngineNavigation();

  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(DataTabs[1]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isModalFilterOpen, setModalFilterOpen] = useState(false);

  const [selectedBlock, setSelectedBlock] = useState<number[]>([]);
  const handleSetSelectedBlock = (blocks: any[]) => {
    setSelectedBlock(
      blocks.map((b: any) =>
        typeof b === "object" ? Number(b.value ?? b.label) : Number(b),
      ),
    );
  };

  const preSelectedDate = useSelector(
    (state: any) => state.modCSPDCLDate.selectedDate,
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modCSPDCLSession.modSession,
  );

  // API data — raw response from getClassBid
  const [apiData, setApiData] = useState<any>(null);
  // Class-grouped data (has generators array per class/slot)
  const [classGroupedData, setClassGroupedData] = useState<any[]>([]);

  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [bidRes, classRes] = await Promise.all([
        getClassBid(),
        preSelectedDate && modSelectedSession
          ? fetchModChartClassGrouped(preSelectedDate, modSelectedSession)
          : Promise.resolve(null),
      ]);
      setApiData((bidRes as any)?.data ?? bidRes ?? null);
      setClassGroupedData((classRes as any)?.data ?? []);
    } catch (err) {
      setError("Failed to fetch bid creation data");
      console.error("Error fetching bid creation data:", err);
    } finally {
      setLoading(false);
    }
  }, [preSelectedDate, modSelectedSession]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData]);

  const downloadTable = () => {
    const raw = (apiData as any)?.data ?? apiData ?? {};
    const classKeys = Object.keys(raw)
      .filter((k) => Array.isArray(raw[k]))
      .sort((a, b) => Number(a) - Number(b));

    if (!classKeys.length) {
      setError("No data available to download!");
      return;
    }

    const workbook = XLSX.utils.book_new();

    classKeys.forEach((classKey) => {
      const rows = transformClassData(raw[classKey]);
      const sheetData = [
        [
          "Time Slot",
          "Demand",
          "TAP",
          "Sur/Def",
          "Demand Met",
          "Class Vol",
          "Class Rate",
          "Single Buy Sum",
          "Single Buy Rate",
          "Block Sum",
          "Block Rate",
          "Single Sell Sum",
          "Single Sell Rate",
        ],
        ...rows.map((r) => [
          `Slot ${r.slotIndex}: (${r.timeRange})`,
          r.demand,
          r.tap,
          r.surDef,
          r.demandMet,
          r.classVol,
          r.classRate,
          r.singleBuyQty,
          r.singleBuyPrice,
          r.blockNetQty,
          r.blockNetPrice,
          r.singleSellQty,
          r.singleSellPrice,
        ]),
      ];
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(workbook, worksheet, `Class ${classKey}`);
    });

    XLSX.writeFile(workbook, "BidCreation_all_classes.xlsx");
  };

  const handleFinalize = async () => {
    try {
      setSubmitLoading(true);
      // TODO: add finalize API call for BidCreation when available
      navigateToPage("bid");
    } catch (err) {
      setError("Failed to finalize bid creation");
      console.error("Error finalizing bid creation:", err);
    } finally {
      setSubmitLoading(false);
    }
  };

  async function handleUpdateReview(localValues: Record<string, any>) {
    if (!Object.keys(localValues).length) return;

    // Merge edits by classKey+slot so price + class_rate can coexist in one item
    type PayloadItem = {
      time_slot__slot: number;
      generator_class: number;
      bid_category?: string;
      price?: number;
      class_rate?: number;
    };
    const merged = new Map<string, PayloadItem>();

    for (const [key, rawValue] of Object.entries(localValues)) {
      // key format: {classKey}__{slot}__{field}
      const parts = key.split("__");
      if (parts.length < 3) continue;
      const classKey = parts[0];
      const slot = Number(parts[1]);
      const field = parts.slice(2).join("__");
      const num = Number(rawValue);
      if (!Number.isFinite(num)) continue;

      const mergeKey = `${classKey}__${slot}`;
      if (!merged.has(mergeKey)) {
        merged.set(mergeKey, {
          time_slot__slot: slot,
          generator_class: Number(classKey),
        });
      }
      const item = merged.get(mergeKey)!;

      if (field === "classRate") {
        item.class_rate = num;
      } else if (field === "singleBuyPrice") {
        // no bid_category → backend defaults to BUY_REPLACEMENT
        item.price = Math.abs(num);
      } else if (field === "singleSellPrice") {
        item.bid_category = "SINGLE_SELL";
        item.price = Math.abs(num);
      } else if (field === "blockNetPrice") {
        item.bid_category = num < 0 ? "BLOCK_SELL" : "BLOCK_BUY";
        item.price = Math.abs(num);
      }
    }

    const payload = Array.from(merged.values()).filter(
      (item) => item.price !== undefined || item.class_rate !== undefined,
    );

    if (!payload.length) {
      setModalOpen(false);
      return;
    }

    try {
      setSubmitLoading(true);
      const res = await postClassBid({ data: payload });
      if (res?.success === false) {
        throw new Error(res.message || "Failed to update bid data");
      }
      setModalOpen(false);
      await handleFetchData();
    } catch (err) {
      setError("Failed to update bid data");
      console.error("postClassBid error:", err);
    } finally {
      setSubmitLoading(false);
    }
  }

  return (
    <div className="page-wrapper">
      <div className="flex flex-col w-full">
        <MainHeader />
        <div className="tabContent">
          <div className="h-[calc(-73px+100vh)] p-4">
            <div className="w-full h-full flex flex-col gap-4">
              <ModHeader title="Bid Creation" />
              <div className="gas-chart-container flex-1 flex flex-col min-h-0 !gap-1">
                <div className="flex justify-between items-center mb-2 border-b border-[#DFEAF2] pb-2">
                  <div className="flex items-center gap-3">
                    <ToggleTab
                      tabs={DataTabs}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                    />
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Select Slot"
                      selectedOptions={selectedBlock}
                      setSelectedOptions={handleSetSelectedBlock as any}
                    />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setModalOpen(true)}
                      className="flex items-center gap-2 p-2 text-sm rounded-xl border border-[#34656D] bg-[#EFFCFF] text-[#34656D] font-medium"
                    >
                      <Edit className="w-5 h-5" />
                      Modify
                    </button>
                    <button
                      onClick={downloadTable}
                      className="flex items-center gap-2 p-2 text-sm rounded-xl bg-[#FFFAF6] text-[#F1935C] font-medium border border-[#F1935C]"
                    >
                      <span>Download Data</span>
                      <Download className="w-5 h-5" />
                    </button>
                    <button
                      disabled={submitLoading}
                      onClick={handleFinalize}
                      className="flex items-center gap-2 p-2 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium"
                    >
                      {submitLoading
                        ? "Processing..."
                        : "Finalize & Create Bid"}
                    </button>
                  </div>
                </div>

                <div className="chartTable flex-1 min-h-0 overflow-hidden">
                  {loading ? (
                    <div className="h-full w-full bg-white flex items-center justify-center">
                      <img
                        src={loader}
                        width={200}
                        alt="loader"
                        className="block mt-[5rem]"
                      />
                    </div>
                  ) : (
                    <>
                      {activeTab === "table" && (
                        <div className="mod-table-load-in h-full min-h-0 flex flex-col">
                          <BidCreationTable
                            setModalOpen={setModalOpen}
                            selectedBlock={selectedBlock}
                            apiData={apiData}
                            classGroupedData={classGroupedData}
                            maxHeight="calc(100vh - 260px)"
                          />
                        </div>
                      )}
                      {activeTab === "chart" && (
                        <BidCreationChart selectedBlock={selectedBlock} />
                      )}
                    </>
                  )}
                </div>
              </div>

              <ErrorPopup error={error} onClose={() => setError(null)} />

              <div className="gna-footer-container">
                <div className="grid grid-cols-3 justify-between gap-5">
                  <Tooltip
                    note={
                      <p>
                        Bid Creation — per-class Block Bid and Single Bid values
                        across all 96 time slots.
                      </p>
                    }
                    text={` 1. Demand Met: MW demand met after this class.
                            2. Class Vol: Total class quantum (MW).
                            3. Class Rate: Variable charge rate (Rs/kWh).
                            4. Block Bid: Buy/Sell block quantum & price.
                            5. Single Bid: Buy/Sell single quantum, MR & price.`}
                  >
                    <p className="gna-footer-text">Source</p>
                  </Tooltip>
                  <p className="gna-footer-text col-span-1 text-center">
                    Powered by <strong>GNA Energy</strong>
                  </p>
                  <strong className="gna-footer-text col-span-1 text-end">
                    Beta Version
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)}>
          <div
            className="w-full flex flex-col"
            style={{ maxHeight: "calc(100vh - 60px)" }}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#DFEAF2]">
              <div className="flex items-center gap-3">
                <h3 className="text-[#525062] text font-semibold">
                  Bid Creation Table
                </h3>
                <span className="inline-flex items-center gap-2 rounded-full border border-[#00B36B] bg-[#F5FFF8] px-3 py-1.5 text-[10px] font-medium leading-none text-[#00B36B] shadow-[0_2px_10px_rgba(0,179,107,0.12)]">
                  <span
                    className="h-2 w-2 rounded-full bg-[#00B36B]"
                    aria-hidden="true"
                  />
                  Modification Mode
                </span>
              </div>
              <button
                onClick={() => setModalFilterOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border border-[#34656D] bg-[#EFFCFF] text-[#34656D] font-medium hover:bg-[#ddf4f9] transition-colors"
              >
                <Edit className="w-4 h-4" />
                Batch Customize
              </button>
            </div>

            <div className="mod-table-load-in flex-1 min-h-0 p-5 pb-2 flex flex-col">
              <BidCreationTable
                setModalOpen={setModalOpen}
                mode="edit"
                handleSubmit={handleUpdateReview}
                loading={submitLoading}
                selectedBlock={selectedBlock}
                apiData={apiData}
                classGroupedData={classGroupedData}
                modalFilter={isModalFilterOpen}
                setModalFilter={setModalFilterOpen}
                maxHeight="calc(100vh - 260px)"
              />
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
}
